import { formatCurrency } from "../utils/price.js"

const ApartmentPriceTable = ({ text, apartmentPrices }) => {
    if (!apartmentPrices || !apartmentPrices.david)
        return null

    // Get current date for schema.org structured data
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonthNum = now.getMonth() + 1 // 1-12

    const getMonthName = (month) => {
        const monthNames = {
            "01": text.january,
            "02": text.february,
            "03": text.march,
            "04": text.april,
            "05": text.may,
            "06": text.june,
            "07": text.july,
            "08": text.august,
            "09": text.september,
            "10": text.october,
            "11": text.november,
            "12": text.december,
        }
        return monthNames[month]
    }

    // Get days in month for validThrough date
    const getDaysInMonth = (year, month) => {
        // month is "01"-"12", convert to 0-11 for Date
        const monthNum = parseInt(month, 10) - 1
        return new Date(year, monthNum + 1, 0).getDate()
    }

    // Determine which year a month belongs to based on current month
    // If we're in December and see "01", it's next year
    // If we're in January and see "12", it's last year (shouldn't happen due to backend filtering)
    const getYearForMonth = (monthStr) => {
        const monthNum = parseInt(monthStr, 10)
        // If month is less than current month, it's likely next year
        // Otherwise it's current year
        if (monthNum < currentMonthNum) {
            return currentYear + 1
        }
        return currentYear
    }

    // Backend already filters to only return future months, so we display all returned prices
    const filteredDavid = apartmentPrices.david || {}
    const filteredJulian = apartmentPrices.julian || {}
    const filteredPhilipp = apartmentPrices.philipp || {}

    // If no prices available, don't render the table
    if (Object.keys(filteredDavid).length === 0) {
        return null
    }

    // Create Schema.org structured data for pricing
    const createPriceSchema = (apartmentName, prices) => {
        const priceOffers = Object.keys(prices).map(month => {
            const year = getYearForMonth(month)
            const daysInMonth = getDaysInMonth(year, month)
            return {
                "@type": "Offer",
                "name": `${apartmentName} - ${getMonthName(month)}`,
                "price": prices[month].toString(),
                "priceCurrency": "EUR",
                "validFrom": `${year}-${month}-01`,
                "validThrough": `${year}-${month}-${String(daysInMonth).padStart(2, '0')}`,
                "availability": "https://schema.org/InStock"
            }
        })

        return {
            "@context": "https://schema.org",
            "@type": "AggregateOffer",
            "itemOffered": {
                "@type": "Accommodation",
                "name": apartmentName
            },
            "offers": priceOffers
        }
    }

    // Create schema for each apartment using filtered prices
    const davidSchema = createPriceSchema("David Apartment", filteredDavid)
    const julianSchema = createPriceSchema("Julian Apartment", filteredJulian)
    const philippSchema = createPriceSchema("Philipp Apartment", filteredPhilipp)

    return (
        <>
            <script type="application/ld+json">
                {JSON.stringify([davidSchema, julianSchema, philippSchema])}
            </script>
            <table className="table" aria-label="Apartment Prices by Month">
                <caption className="visually-hidden">
                    {text.pricesCaption || "Monthly prices for our farm apartments"}
                </caption>
                <thead>
                    <tr>
                        <th scope="col">{text.pricesMonth}</th>
                        <th scope="col" id="david">David</th>
                        <th scope="col" id="julian">Julian</th>
                        <th scope="col" id="philipp">Philipp</th>
                    </tr>
                </thead>
                <tbody>
                    {Object.keys(filteredDavid).sort((a, b) => {
                        // Sort months correctly: current month onwards, then wrap to next year
                        const monthA = parseInt(a, 10)
                        const monthB = parseInt(b, 10)
                        // If both are in current year range (>= current month), sort normally
                        if (monthA >= currentMonthNum && monthB >= currentMonthNum) {
                            return monthA - monthB
                        }
                        // If both are in next year range (< current month), sort normally
                        if (monthA < currentMonthNum && monthB < currentMonthNum) {
                            return monthA - monthB
                        }
                        // Current year months come before next year months
                        if (monthA >= currentMonthNum) return -1
                        if (monthB >= currentMonthNum) return 1
                        return 0
                    }).map((month) => {
                        const priceDavid = filteredDavid[month]
                        const priceJulian = filteredJulian[month]
                        const pricePhilipp = filteredPhilipp[month]
                        const year = getYearForMonth(month)
                        const monthName = getMonthName(month)

                        return (
                            <tr key={`${year}-${month}`}>
                                <th scope="row">{monthName} {year}</th>
                                <td headers="david" aria-label={`David ${monthName} ${year}`}>{formatCurrency(priceDavid)}</td>
                                <td headers="julian" aria-label={`Julian ${monthName} ${year}`}>{formatCurrency(priceJulian)}</td>
                                <td headers="philipp" aria-label={`Philipp ${monthName} ${year}`}>{formatCurrency(pricePhilipp)}</td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </>
    )
}

export default ApartmentPriceTable
